import { zod } from '@acme/core'
import dayjs from 'dayjs'
import { z } from 'zod'
import ApiService from '../api.service'

export type Abono = z.infer<typeof zod.AbonoSchema>

export const recorrenciaAbonoSchema = z
    .object({
        tipoRepeticao: z.enum(['DIARIO', 'SEMANAL', 'MENSAL', 'NAO_REPETE']),
        every: z.coerce.number().int().nonnegative().default(1),
        repeticaoMensalOpcao: z.enum(['DIA', 'SEMANA']).default('DIA'),
        dataFinal: z
            .date()
            .or(z.string())
            .optional()
            .transform(val => val || undefined),
    })
    .refine(
        data => {
            if (data.tipoRepeticao === 'NAO_REPETE') return true
            return data.dataFinal !== undefined
        },
        {
            message: 'Data final é obrigatória para repetições',
            path: ['dataFinal'],
        },
    )
export type RecorrenciaAbono = z.infer<typeof recorrenciaAbonoSchema>

export const createAbonoSchema = z
    .object({
        tipo: z.enum(['NORMAL', 'SAUDE_OCUPACIONAL']),
        fileUrl: z.any().optional(),
        data: z.date().or(z.string().refine(Boolean)),
        justificativa: z.string().trim().max(190).optional().default(''),
        descricao: z.string().trim().max(30).nullish().default(null),
        quantidadeHoras: z.coerce
            .string()
            .min(1, 'Obrigatório')
            .regex(new RegExp('^[0-9]{1,2}:[0-5][0-9]$')),
        funcionarioId: z
            .array(z.number().positive())
            .or(
                z
                    .number()
                    .positive()
                    .transform(v => [v]),
            )
            .refine(arr => arr.length > 0, {
                message: 'Informe ao menos um funcionário',
            }),
    })
    .refine(data => data.descricao || data.justificativa, {
        message: 'Informe uma descrição ou justificativa',
        path: ['justificativa'],
    })

export const createAbonoRecorrenteSchema = createAbonoSchema
    .and(recorrenciaAbonoSchema)
    .refine(
        // data final deve ser maior que data inicial
        data => {
            if (data.tipoRepeticao === 'NAO_REPETE') return true
            const dtInicial = dayjs(data.data)
            const dtFinal = dayjs(data.dataFinal)
            return dtFinal.isAfter(dtInicial, 'day')
        },
        {
            message: 'Data final deve ser maior que data inicial',
            path: ['dataFinal'],
        },
    )
    .refine(
        // se houver repetição, período não pode superar 1 ano
        data => {
            if (data.tipoRepeticao === 'NAO_REPETE') return true
            const dtInicial = dayjs(data.data)
            const dtFinal = dayjs(data.dataFinal)
            return dtFinal.isBefore(dtInicial.add(1, 'year'), 'day')
        },
        {
            message: 'O período de repetição não pode ser maior que 1 ano',
            path: ['dataFinal'],
        },
    )

const service = new ApiService<Abono | Abono[]>('abonos')

export const abonos = Object.assign(service, {
    aprovacao: new ApiService<Abono>('abonos/aprovacao'),
    reprovacao: new ApiService<Abono>('abonos/reprovacao'),
})
